import { CloseRounded } from "@mui/icons-material";
import { alpha, IconButton, MobileStepper, Stack } from "@mui/material";
import { useEffect } from "react";
import { COLOR_MAIN, COLOR_WHITE } from "../../../data/colors/Colors";
import { CustomButtonView } from "../../component/CustomButtonView";
import { NewGamePage, NewGameViewModel } from "./NewGameViewModel";
import { useTranslation } from "react-i18next";
import { CustomCardView } from "../../component/CustomCardView";
import { CustomTextView } from "../../component/CustomTextView";
import { ChooseTeamsNameView } from "./chooseTeamsName/ChooseTeamsNameView";
import { ChooseCategoriesView } from "./chooseCategories/ChooseCategoriesView";
import { Category } from "../../../domain/model/Category";
import {
  provideCreateGameUseCase,
  provideGetAllCategoriesUseCase,
} from "../../di/UseCasesModule";
import useWindowDimensions from "../../../core/utils/UseWindowDimensions";
import { Player } from "../../../domain/model/User";

export function NewGameView({
  handleClose,
  handleHeightChanged,
  player,
  shouldChooseCategoryDirectly,
}: {
  handleClose: () => void;
  handleHeightChanged: (height: string) => void;
  player: Player;
  shouldChooseCategoryDirectly: boolean;
}) {
  const viewModel = NewGameViewModel(
    player,
    provideGetAllCategoriesUseCase(),
    provideCreateGameUseCase(),
  );

  const { t } = useTranslation();

  const { width } = useWindowDimensions();

  const activeStep = viewModel.activeStep;
  const handleNext = viewModel.handleNext;
  const handleBack = viewModel.handleBack;

  useEffect(() => {
    switch (viewModel.currentPage) {
      case NewGamePage.CHOOSE_CATEGORIES: {
        handleHeightChanged("100%");
        break;
      }
      default: {
        handleHeightChanged(width < 700 ? "80%" : "65%");
        break;
      }
    }
  }, [viewModel.currentPage]);

  return (
    <Stack
      width={"100%"}
      alignItems={"center"}
      p={1}
      height={"100%"}
      justifyContent={"space-between"}
      spacing={1}
    >
      <CustomCardView
        width={"100%"}
        padding={2}
        borderRadius={4}
        background={COLOR_MAIN}
        children={
          <Stack
            width={"100%"}
            height={"100%"}
            justifyContent={"space-between"}
            alignItems={"center"}
            direction={"row"}
          >
            <CustomTextView
              text={CurrentPageTitleView({
                currentPage: viewModel.currentPage,
              })}
              fontWeight="bold"
              variant="h5"
              textColor="white"
            />
            <IconButton
              onClick={handleClose}
              sx={{
                width: 40,
                height: 40,
                background: alpha(COLOR_WHITE, 0.2),
              }}
            >
              <CloseRounded sx={{ color: "white" }} />
            </IconButton>
          </Stack>
        }
      />
      <CustomCardView
        width={"100%"}
        padding={0}
        borderRadius={4}
        height={"100%"}
        children={
          <CurrentPageView
            player={player}
            currentPage={viewModel.currentPage}
            firstTeamName={viewModel.firstTeamName}
            secondTeamName={viewModel.secondTeamName}
            onFirstTeamNameChanged={viewModel.handleOnFirstTeamNameChanged}
            onSecondTeamNameChanged={viewModel.handleOnSecondTeamNameChanged}
            categories={viewModel.categories}
            setSelectedCategory={viewModel.setSelectedCategory}
            selectedCategory={viewModel.selectedCategory}
            loading={viewModel.loading}
            onCategoryClicked={viewModel.onCategoryClicked}
          />
        }
      />
      <CustomCardView
        width={"100%"}
        padding={2}
        borderRadius={4}
        background={COLOR_MAIN}
        children={
          <Stack
            alignItems={"center"}
            width={"100%"}
            height={"100%"}
            justifyContent={"center"}
          >
            <MobileStepper
              variant="progress"
              steps={viewModel.totalPages}
              position="static"
              activeStep={activeStep}
              sx={{
                width: "100%",
                background: "transparent",
              }}
              LinearProgressProps={{
                sx: {
                  borderRadius: 20,
                  backgroundColor: "rgba(255, 255, 255, 0.3)",
                  width: "100%",
                  marginLeft: 1,
                  marginRight: 1,
                  "& .MuiLinearProgress-bar": {
                    backgroundColor: "white",
                    borderRadius: 20,
                  },
                },
              }}
              nextButton={
                <CustomButtonView
                  text={
                    activeStep === viewModel.totalPages - 1
                      ? t("start")
                      : t("next")
                  }
                  onClick={
                    activeStep === viewModel.totalPages - 1
                      ? () => viewModel.startGame()
                      : handleNext
                  }
                  fontSize="button"
                  backgroundColor={alpha(COLOR_WHITE, 0.2)}
                  loading={viewModel.gameStartingLoading}
                />
              }
              backButton={
                <CustomButtonView
                  text={t("previous")}
                  onClick={handleBack}
                  fontSize="button"
                  disabled={activeStep === 0 || viewModel.gameStartingLoading}
                  props={{ paddingLeft: 4, paddingRight: 4 }}
                  backgroundColor={alpha(COLOR_WHITE, 0.2)}
                />
              }
            />
          </Stack>
        }
      />
    </Stack>
  );
}

function CurrentPageTitleView({
  currentPage,
}: {
  currentPage: NewGamePage;
}): string {
  const { t } = useTranslation();
  switch (currentPage) {
    case NewGamePage.CHOOSE_NAME:
      return t("choose_teams_name");
    case NewGamePage.CHOOSE_CATEGORIES:
      return t("choose_categories");
  }
}

function CurrentPageView({
  player,
  currentPage,
  firstTeamName,
  secondTeamName,
  onFirstTeamNameChanged,
  onSecondTeamNameChanged,
  categories,
  selectedCategory,
  setSelectedCategory,
  loading,
  onCategoryClicked,
}: {
  player: Player;
  currentPage: NewGamePage;
  firstTeamName: string;
  secondTeamName: string;
  onFirstTeamNameChanged: (txt: string) => void;
  onSecondTeamNameChanged: (txt: string) => void;
  categories: Array<Category>;
  selectedCategory: Category | undefined;
  setSelectedCategory: (category: Category) => void;
  loading: boolean;
  onCategoryClicked: (category: Category) => void;
}) {
  switch (currentPage) {
    case NewGamePage.CHOOSE_NAME:
      return (
        <ChooseTeamsNameView
          onFirstTeamNameChanged={onFirstTeamNameChanged}
          onSecondTeamNameChanged={onSecondTeamNameChanged}
          firstTeamName={firstTeamName}
          secondTeamName={secondTeamName}
        />
      );
    case NewGamePage.CHOOSE_CATEGORIES:
      return (
        <ChooseCategoriesView
          player={player}
          categories={categories}
          selectedCategory={selectedCategory}
          setSelectedCategory={setSelectedCategory}
          loading={loading}
          onCategoryClicked={onCategoryClicked}
        />
      );
  }
}
